import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Loader2, Calendar, Users, MapPin, User, Mail, Phone } from 'lucide-react';

const MultiStepForm = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [errors, setErrors] = useState({});
  const [urlParams, setUrlParams] = useState({});
  const [userIP, setUserIP] = useState('');
  
  const [formData, setFormData] = useState({
    birth_day: '',
    birth_month: '',
    birth_year: '',
    current_insurer: '',
    family_members: '',
    zip_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: ''
  });

  // Extract URL parameters and get user IP when component mounts
  React.useEffect(() => {
    const extractUrlParams = () => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = {};
      
      // Extract tid, sid, aid if they exist
      if (urlSearchParams.get('tid')) params.tid = urlSearchParams.get('tid');
      if (urlSearchParams.get('sid')) params.sid = urlSearchParams.get('sid');
      if (urlSearchParams.get('aid')) params.aid = urlSearchParams.get('aid');
      
      setUrlParams(params);
    };

    const getUserIP = async () => {
      try {
        const response = await fetch('https://ipapi.co/ip/');
        const ip = await response.text();
        setUserIP(ip.trim());
      } catch (error) {
        console.error('Failed to get user IP:', error);
        setUserIP(''); // Fallback to empty string if IP detection fails
      }
    };

    extractUrlParams();
    getUserIP();
  }, []);

  // Generate arrays for dropdowns
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];
  const years = Array.from({ length: 73 }, (_, i) => 2007 - i); // 2007 down to 1935

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.birth_day) {
          newErrors.birth_day = 'El día es obligatorio';
        }
        if (!formData.birth_month) {
          newErrors.birth_month = 'El mes es obligatorio';
        }
        if (!formData.birth_year) {
          newErrors.birth_year = 'El año es obligatorio';
        }
        break;
      case 2:
        if (!formData.current_insurer) {
          newErrors.current_insurer = 'Por favor selecciona una opción';
        }
        break;
      case 3:
        if (!formData.family_members) {
          newErrors.family_members = 'Por favor selecciona el número de miembros familiares';
        }
        break;
      case 4:
        if (!formData.zip_code) {
          newErrors.zip_code = 'El código postal es obligatorio';
        } else if (!/^\d{5}$/.test(formData.zip_code)) {
          newErrors.zip_code = 'El código postal debe tener exactamente 5 dígitos';
        }
        break;
      case 5:
        if (!formData.first_name || formData.first_name.length < 3) {
          newErrors.first_name = 'El nombre debe tener al menos 3 caracteres';
        } else if (formData.first_name.length > 255) {
          newErrors.first_name = 'El nombre debe tener menos de 255 caracteres';
        }
        if (!formData.last_name || formData.last_name.length < 3) {
          newErrors.last_name = 'El apellido debe tener al menos 3 caracteres';
        } else if (formData.last_name.length > 255) {
          newErrors.last_name = 'El apellido debe tener menos de 255 caracteres';
        }
        break;
      case 6:
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email) {
          newErrors.email = 'El email es obligatorio';
        } else if (!emailRegex.test(formData.email) || formData.email.length > 255) {
          newErrors.email = 'Por favor introduce una dirección de email válida';
        }
        if (!formData.phone_number) {
          newErrors.phone_number = 'El número de teléfono es obligatorio';
        } else if (!/^\d{8,12}$/.test(formData.phone_number)) {
          newErrors.phone_number = 'El número de teléfono debe tener entre 8 y 12 dígitos';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getStepMessage = () => {
    switch (currentStep) {
      case 1:
        // Calculate age from birth date
        if (formData.birth_year) {
          const age = new Date().getFullYear() - parseInt(formData.birth_year);
          if (age < 40) {
            return "Aunque pueda parecer prematuro pensar en esto, planificar con anticipación significa que tus seres queridos nunca tendrán que enfrentarse a costos inesperados o estrés en el futuro.";
          } else if (age >= 40 && age <= 49) {
            return "Esta es la edad en la que la mayoría de las familias comienzan a planificar en serio. Un pequeño paso hoy significa una gran protección para mañana.";
          } else if (age >= 50 && age <= 59) {
            return "Has trabajado duro por tu familia. Asegurar que estén protegidos de los gastos funerarios es una de las cosas más consideradas que puedes hacer.";
          } else if (age >= 60 && age <= 69) {
            return "La tranquilidad no tiene precio. Con una póliza de decesos, tu familia no tendrá que preocuparse por costos o papeleo cuando llegue el momento.";
          } else if (age >= 70 && age <= 79) {
            return "En esta etapa, se trata de disfrutar la vida sin preocupaciones. Tu familia merece paz, no papeleo y facturas inesperadas.";
          } else if (age >= 80) {
            return "Nunca es demasiado tarde para asegurar que tu familia se libre del estrés y altos gastos funerarios. Este es un regalo de amor.";
          }
        }
        return "";
      
      case 2:
        if (formData.current_insurer === "No") {
          return "Sin seguro de decesos, las familias a menudo enfrentan facturas de €4,000–€10,000 inesperadamente. Pero puedes prevenir esto hoy.";
        } else if (formData.current_insurer) {
          return "Excelente elección — más del 50% de los españoles ya tienen seguro de decesos. Asegurémonos de que estés pagando el mejor precio por tu cobertura.";
        }
        return "";
      
      case 3:
        if (formData.family_members === "1") {
          return "Se trata de ti. Cuidar tu propia despedida asegura dignidad y paz para ti y menos estrés para tus seres queridos.";
        } else if (formData.family_members === "5+") {
          return "Una familia grande significa mucho amor. Con el plan correcto, puedes proteger a todos — y ahorrarles estrés financiero y emocional.";
        } else if (formData.family_members) {
          return "Estás pensando en tu familia. Protegerlos significa que nunca tendrán que preocuparse por costos o procedimientos complicados.";
        }
        return "";
      
      case 4:
        if (formData.zip_code) {
          return "Los costos funerarios en tu área pueden alcanzar más de €4,000. Con seguro, esos gastos están completamente cubiertos desde solo €3/mes.";
        }
        return "";
      
      case 5:
        if (formData.first_name) {
          return `Gracias, ${formData.first_name}. Dar este paso muestra cuánto te preocupas por el futuro de tu familia.`;
        }
        return "";
      
      case 6:
        return "Te llamaremos pronto con la mejor oferta. No se trata solo de ahorrar dinero — se trata de proteger la tranquilidad de tu familia.";
      
      default:
        return "";
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 6) {
        handleSubmit();
      } else if (currentStep === 4) {
        // Show analysing animation for postal code
        setIsAnalysing(true);
        setTimeout(() => {
          setIsAnalysing(false);
          setShowMessage(true);
        }, 2500); // 2.5 seconds of analysis
      } else {
        setShowMessage(true);
      }
    }
  };

  const handleContinue = () => {
    setShowMessage(false);
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    try {
      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: formData.phone_number,
        zip_code: formData.zip_code,
        birth_year: parseInt(formData.birth_year),
        birth_month: formData.birth_month.padStart(2, '0'),
        birth_day: formData.birth_day.padStart(2, '0'),
        current_insurer: formData.current_insurer,
        family_members: formData.family_members,
        utm_source: "website",
        utm_campaign: "funeral_insurance_form",
        timestamp_iso: new Date().toISOString(),
        // Add URL parameters if they exist
        ...(urlParams.tid && { tid: urlParams.tid }),
        ...(urlParams.sid && { sid: urlParams.sid }),
        ...(urlParams.aid && { aid: urlParams.aid }),
        // Add user IP address
        ipAddress: userIP
      };

      const response = await fetch('https://hook.eu2.make.com/oq7xb5iu9tyr6wt8fl7y8tsb798occ4c', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setTimeout(() => {
          setIsLoading(false);
          setIsCompleted(true);
        }, 4000); // Increased from 2000ms to 4000ms (4 seconds)
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setIsLoading(false);
      // Handle error - maybe show error message
    }
  };

  const handleClose = () => {
    // Reset form state
    setCurrentStep(1);
    setIsLoading(false);
    setIsCompleted(false);
    setShowMessage(false);
    setIsAnalysing(false);
    setErrors({});
    setFormData({
      birth_day: '',
      birth_month: '',
      birth_year: '',
      current_insurer: '',
      family_members: '',
      zip_code: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: ''
    });
    onClose();
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const renderStep = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          <p className="text-xl font-semibold text-gray-900">🧐 ¡Buscando OTRAS ofertas para ti!</p>
          <p className="text-gray-600">Por favor espera un momento...</p>
        </div>
      );
    }

    if (isAnalysing) {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-6">
          <div className="text-center space-y-4">
            <div className="text-6xl">🔍</div>
            <p className="text-xl font-semibold text-gray-900">Analizando tu zona...</p>
            <p className="text-gray-600">Obteniendo los mejores datos para el código postal {formData.zip_code}</p>
          </div>
          
          {/* Animated Progress Bar */}
          <div className="w-full max-w-xs">
            <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full animate-pulse loading-bar"></div>
            </div>
            <p className="text-sm text-gray-500 mt-2 text-center">Calculando costos funerarios en tu región...</p>
          </div>
        </div>
      );
    }

    if (isCompleted) {
      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-6">
          <div className="text-center space-y-4">
            <p className="text-2xl font-bold text-gray-900">🥳 ¡Gracias por confiar en nosotros!</p>
            <p className="text-gray-600">
              Vamos a conectarte con las siguientes ofertas — ¡mantente cerca de tu teléfono!
            </p>
          </div>
          
          <Card className="w-full max-w-md border-2 border-blue-200">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-lg text-blue-900">Seguro de decesos desde €3/mes</CardTitle>
              <CardDescription className="text-blue-700">
                La mejor manera de cubrir tus gastos funerarios al mejor precio.
              </CardDescription>
            </CardHeader>
          </Card>
          
          <Button onClick={handleClose} className="w-full max-w-md bg-blue-600 hover:bg-blue-700">
            Cerrar
          </Button>
        </div>
      );
    }

    // Show emotional message after step completion
    if (showMessage) {
      const message = getStepMessage();
      const getStepIcon = () => {
        switch (currentStep) {
          case 1: return "🧓";
          case 2: return "📝";
          case 3: return "👨‍👩‍👧";
          case 4: return "📍";
          case 5: return "📧";
          case 6: return "📞";
          default: return "💙";
        }
      };

      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-6">
          <div className="text-center space-y-4">
            <div className="text-6xl">{getStepIcon()}</div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 max-w-md">
              <p className="text-lg text-gray-800 leading-relaxed font-medium">
                {message}
              </p>
            </div>
          </div>
          
          <Button 
            onClick={handleContinue}
            className="w-full max-w-xs bg-blue-600 hover:bg-blue-700 text-white py-3 font-semibold"
          >
            Continuar
          </Button>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Calendar className="w-12 h-12 mx-auto text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">¿Cuál es tu fecha de nacimiento?</h3>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="day" className="text-sm font-medium text-gray-700">Día</Label>
                <Select
                  value={formData.birth_day}
                  onValueChange={(value) => updateFormData('birth_day', value)}
                >
                  <SelectTrigger className={`border-2 ${errors.birth_day ? 'border-red-500' : 'border-gray-200'} focus:border-blue-500`}>
                    <SelectValue placeholder="Día" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {days.map(day => (
                      <SelectItem key={day} value={day.toString()}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.birth_day && (
                  <p className="text-xs text-red-500">{errors.birth_day}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="month" className="text-sm font-medium text-gray-700">Mes</Label>
                <Select
                  value={formData.birth_month}
                  onValueChange={(value) => updateFormData('birth_month', value)}
                >
                  <SelectTrigger className={`border-2 ${errors.birth_month ? 'border-red-500' : 'border-gray-200'} focus:border-blue-500`}>
                    <SelectValue placeholder="Mes" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {months.map(month => (
                      <SelectItem key={month.value} value={month.value.toString()}>{month.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.birth_month && (
                  <p className="text-xs text-red-500">{errors.birth_month}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="year" className="text-sm font-medium text-gray-700">Año</Label>
                <Select
                  value={formData.birth_year}
                  onValueChange={(value) => updateFormData('birth_year', value)}
                >
                  <SelectTrigger className={`border-2 ${errors.birth_year ? 'border-red-500' : 'border-gray-200'} focus:border-blue-500`}>
                    <SelectValue placeholder="Año" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {years.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.birth_year && (
                  <p className="text-xs text-red-500">{errors.birth_year}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Users className="w-12 h-12 mx-auto text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">¿Tienes actualmente seguro de decesos?</h3>
            </div>
            
            <div className="space-y-4">
              <RadioGroup
                value={formData.current_insurer}
                onValueChange={(value) => updateFormData('current_insurer', value)}
                className="grid grid-cols-2 gap-3"
              >
                <div className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <RadioGroupItem value="No" id="no" className="text-blue-600" />
                  <Label htmlFor="no" className="flex-1 cursor-pointer font-medium">No</Label>
                </div>
                <div className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <RadioGroupItem value="Sí, con Santalucía" id="santalucia" className="text-blue-600" />
                  <Label htmlFor="santalucia" className="flex-1 cursor-pointer font-medium text-sm">Sí, con Santalucía</Label>
                </div>
                <div className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <RadioGroupItem value="Aura" id="aura" className="text-blue-600" />
                  <Label htmlFor="aura" className="flex-1 cursor-pointer font-medium">Aura</Label>
                </div>
                <div className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <RadioGroupItem value="Preventiva" id="preventiva" className="text-blue-600" />
                  <Label htmlFor="preventiva" className="flex-1 cursor-pointer font-medium">Preventiva</Label>
                </div>
                <div className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <RadioGroupItem value="DKV" id="dkv" className="text-blue-600" />
                  <Label htmlFor="dkv" className="flex-1 cursor-pointer font-medium">DKV</Label>
                </div>
                <div className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <RadioGroupItem value="Otro" id="other" className="text-blue-600" />
                  <Label htmlFor="other" className="flex-1 cursor-pointer font-medium">Otro</Label>
                </div>
              </RadioGroup>
              {errors.current_insurer && (
                <p className="text-sm text-red-500">{errors.current_insurer}</p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Users className="w-12 h-12 mx-auto text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">¿Cuántos miembros de la familia quieres incluir?</h3>
            </div>
            
            <div className="space-y-4">
              <RadioGroup
                value={formData.family_members}
                onValueChange={(value) => updateFormData('family_members', value)}
                className="grid grid-cols-2 gap-3"
              >
                {['1', '2', '3', '4', '5+'].map((number) => (
                  <div key={number} className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                    <RadioGroupItem value={number} id={`family-${number}`} className="text-blue-600" />
                    <Label htmlFor={`family-${number}`} className="flex-1 cursor-pointer font-medium text-center">
                      {number} {number === '1' ? 'miembro' : 'miembros'}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {errors.family_members && (
                <p className="text-sm text-red-500">{errors.family_members}</p>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <MapPin className="w-12 h-12 mx-auto text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">¿Cuál es tu código postal?</h3>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="zipCode" className="text-sm font-medium text-gray-700">Código Postal</Label>
              <Input
                id="zipCode"
                type="text"
                placeholder="12345"
                value={formData.zip_code}
                onChange={(e) => updateFormData('zip_code', e.target.value.replace(/\D/g, '').slice(0, 5))}
                className={`border-2 ${errors.zip_code ? 'border-red-500' : 'border-gray-200'} focus:border-blue-500 text-lg p-4`}
                maxLength={5}
              />
              {errors.zip_code && (
                <p className="text-sm text-red-500">{errors.zip_code}</p>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <User className="w-12 h-12 mx-auto text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">Tu Nombre</h3>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">Nombre</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="María"
                  value={formData.first_name}
                  onChange={(e) => updateFormData('first_name', e.target.value)}
                  className={`border-2 ${errors.first_name ? 'border-red-500' : 'border-gray-200'} focus:border-blue-500 text-lg p-4`}
                />
                {errors.first_name && (
                  <p className="text-sm text-red-500">{errors.first_name}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">Apellido</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="García"
                  value={formData.last_name}
                  onChange={(e) => updateFormData('last_name', e.target.value)}
                  className={`border-2 ${errors.last_name ? 'border-red-500' : 'border-gray-200'} focus:border-blue-500 text-lg p-4`}
                />
                {errors.last_name && (
                  <p className="text-sm text-red-500">{errors.last_name}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="flex justify-center space-x-2">
                <Mail className="w-8 h-8 text-blue-600" />
                <Phone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Datos de Contacto</h3>
              <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                ¡Este es el último paso! Una vez completado, te llamaremos con la mejor oferta que encontremos para ti.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Dirección de Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="maria@ejemplo.com"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  className={`border-2 ${errors.email ? 'border-red-500' : 'border-gray-200'} focus:border-blue-500 text-lg p-4`}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Número de Teléfono</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="612345678"
                  value={formData.phone_number}
                  onChange={(e) => updateFormData('phone_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
                  className={`border-2 ${errors.phone_number ? 'border-red-500' : 'border-gray-200'} focus:border-blue-500 text-lg p-4`}
                />
                {errors.phone_number && (
                  <p className="text-sm text-red-500">{errors.phone_number}</p>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto border-2 border-gray-200 shadow-xl">
        <DialogHeader className="border-b border-gray-100 pb-4">
          <DialogTitle className="text-center">
            {!isLoading && !isCompleted && (
              <div className="flex items-center justify-center space-x-2">
                <span className="text-lg font-semibold text-gray-700">Paso {currentStep} de 6</span>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5, 6].map((step) => (
                    <div
                      key={step}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        step <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-6">
          {renderStep()}
        </div>
        
        {!isLoading && !isCompleted && !showMessage && !isAnalysing && (
          <div className="flex space-x-3 pt-4 border-t border-gray-100">
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1 border-2 border-gray-300 hover:border-gray-400 text-gray-700 py-3"
              >
                Atrás
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 font-semibold"
            >
              {currentStep === 6 ? 'Enviar' : 'Siguiente'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MultiStepForm;